<template>
    <QuestionBox :title="step.question" :text="$t('module.dragDropOnImageText')" />
    <div class="m-dragDropOnImage u-posRelative" ref="El">
        <div class="m-dragDropOnImage__imageHolder u-posRelative">
            <img :src="step.image" class="u-cover u-br">
            <template v-for="box in boxes" :key="`target${box.id}`">
                <div 
                    class="m-dragDropOnImage__target"
                    :style="{
                        left: box.target.x + '%',
                        top: box.target.y + '%',
                        width: box.target.width + '%',
                        height: box.target.height + '%'
                    }"
                >
                </div>
            </template>
            <div class="m-dragDropOnImage__dropzone" v-drop="{ onDrop: onDrop, draggingClass: '-dragging' }"></div>
        </div>

        <div class="m-dragDropOnImage__items m-block u-bgWhite u-border u-br u-mt20 u-shadow">
            <div class="m-dragDropOnImage__placeholders u-py20 u-px50">
                <template v-for="box in boxes" :key="`drag${box.id}`">
                    <div :class="{ '-dropped': box.dropped }" class="m-dragDropOnImage__itemPlaceholder">
                        
                    </div>
                </template>
            </div>
            <div class="m-dragDropOnImage__itemsHolder u-py20 u-px50">
                <div class="m-dragDropOnImage__itemsInner">
                    <template v-for="box in boxes" :key="`drag${box.id}`">
                        <div 
                            v-drag="{ dragData: box, onDragStart: onDragStart, draggingClass: '-dragging' }"
                            :class="{ '-dropped': box.dropped, '-success': ( showErrors && box.success ), '-error': ( showErrors && ! box.success ) }"
                            :style="box.dropped ? {
                                zIndex: 100,
                                position: 'absolute',
                                left: box.x + 'px',
                                top: box.y + 'px'
                            } : {}"
                            class="m-dragDropOnImage__item"
                            ref="item"
                        >
                            <div class="m-dragDropOnImage__itemInner">
                                <svg class="m-dragDropOnImage__itemIcon u-posRelative" width="6" height="26" viewBox="0 0 6 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.28395 18.3772L0.724365 0.172073H5.27563L4.75336 18.3772H1.28395ZM0.836281 25.0548V21.1751H5.20102V25.0548H0.836281Z" fill="#D40000"/>
                                </svg>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>

    <FixedButton 
        :isDisabled="!allDropped" 
        @click="handleButtonClick" 
        :buttonClass="showTryAgain ? '-reset' : ''">
        {{ showTryAgain ? $t('module.tryAgain') : (answeredCorrect ? $t('module.nextQuestion') : $t('module.checkQuestion')) }}
    </FixedButton>

</template>

<script setup>

import { ref, computed, inject, onMounted } from 'vue';
import 'drag-drop-touch'
import { cloneDeep } from 'lodash';
import { useI18n } from 'vue-i18n';
import { addAttempt } from '../../utils/globalFunctions';

// Components
import QuestionBox from './QuestionBox.vue';
import FixedButton from './FixedButton.vue';

const props = defineProps({
    step: {
        type: Object,
    },
});

const { t } = useI18n();
const mouseOffset = ref({ x: 0, y: 0 });
const selectedAnswer = ref(null);
const El = ref(null);
const showTryAgain = ref(false);
const answeredCorrect = ref(false);

const completeStep = inject('completeStep');
const showNotice = inject('showNotice');
const hideNotice = inject('hideNotice');

const attempts = defineModel( 'attempts' );

const rectanglesJson = JSON.parse(props.step.json);
const initBoxes = rectanglesJson.map((item, index) => ({
    id: index + 1,
    dropped: false,
    success: false,
    x: null,
    y: null,
    target: {
        x: parseFloat(item.x),
        y: parseFloat(item.y),
        width: parseFloat(item.width),
        height: parseFloat(item.height)
    }
}));

const boxes = ref(cloneDeep(initBoxes));
const showErrors = ref(false);
const showReset = ref(false);
const usedTargets = ref(new Set());

const draggedBox = ref({ width: 60, height: 50 });

const reset = () => {
    El.value.classList.remove('-disableDrag');
    boxes.value = cloneDeep(initBoxes);
    selectedAnswer.value = null;
    showTryAgain.value = false;
    showErrors.value = false;
    usedTargets.value.clear();
    hideNotice();
}

const checkAnswer = () => {
    if (!allDropped.value) {
        return;
    }

    El.value.classList.add('-disableDrag');

    showErrors.value = true;

    if (!allAnswersCorrect.value) {
        showTryAgain.value = true; 
        attempts.value.push( addAttempt( props.step.uuid, false ) );
        showNotice(t('module.wrongDragDropOnImageTitle'), t('module.wrongDragDropOnImageText'), '-error');

        return;
    }

    attempts.value.push( addAttempt( props.step.uuid, true ) );
    completeStep(props.step.uuid, false, true);
    answeredCorrect.value = true;
    const textCorrect = props.step.feedback_correct ? props.step.feedback_correct : t('module.correctAnswerText');
    showNotice(t('module.correctAnswerTitle'), textCorrect, '-success');
}

const handleButtonClick = () => {
    if (showTryAgain.value) {
        reset();
    } else if (answeredCorrect.value) {
        completeStep(props.step.uuid, true, false);
        hideNotice();
    } else {
        checkAnswer();
    }
}

function onDragStart(domEl, dragEvent, dragOptions, options) {
    showErrors.value = false;
    showReset.value = false;

    const rect = domEl.getBoundingClientRect();
    mouseOffset.value = {
        x: dragEvent.clientX - rect.left,
        y: dragEvent.clientY - rect.top
    };

    draggedBox.value.width = rect.width;
    draggedBox.value.height = rect.height;

    const boxIndex = boxes.value.findIndex(box => box.id === dragOptions.dragData.id);
}

function onDrop(domEl, dragEvent, dragOptions, options) {
    const dropZoneRect = domEl.getBoundingClientRect();
    const boxIndex = boxes.value.findIndex(box => box.id === dragOptions.dragData.id);

    if (boxIndex !== -1) {
        const x = dragEvent.clientX - dropZoneRect.left - mouseOffset.value.x;
        const y = dragEvent.clientY - dropZoneRect.top - mouseOffset.value.y;
        boxes.value[boxIndex].x = x;
        boxes.value[boxIndex].y = y;
        boxes.value[boxIndex].dropped = true;

        usedTargets.value.clear();

        boxes.value.forEach((box, idx) => {
            let isOnTarget = false;

            boxes.value.forEach((targetBox, targetIdx) => {
                const targetPixels = {
                    x: (targetBox.target.x / 100) * dropZoneRect.width,
                    y: (targetBox.target.y / 100) * dropZoneRect.height,
                    width: (targetBox.target.width / 100) * dropZoneRect.width,
                    height: (targetBox.target.height / 100) * dropZoneRect.height
                };

                const isCollision = !(
                    box.x + draggedBox.value.width < targetPixels.x ||
                    box.x > targetPixels.x + targetPixels.width ||
                    box.y + draggedBox.value.height < targetPixels.y ||
                    box.y > targetPixels.y + targetPixels.height
                );

                if (isCollision && !usedTargets.value.has(targetIdx)) {
                    box.success = true;
                    isOnTarget = true;
                    usedTargets.value.add(targetIdx);
                }
            });

            if (!isOnTarget) {
                box.success = false;
            }
        });

        boxes.value = [...boxes.value];
    }
}



const allDropped = computed(() => {
    return boxes.value.every(box => box.dropped === true);
});

const allAnswersCorrect = computed(() => {
    return boxes.value.every(box => box.success === true);
});

</script>
